import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Alert from '@cloudscape-design/components/alert'
import FormField from '@cloudscape-design/components/form-field'
import Badge from '@cloudscape-design/components/badge'
import Link from '@cloudscape-design/components/link'
import Multiselect from '@cloudscape-design/components/multiselect'
import type { MultiselectProps } from '@cloudscape-design/components/multiselect'
import type { BuyerJourneyStage, ContentType, AutonomousResult, NodeName, NodeStatus, StreamPublishQueueItem } from '@/lib/types'
import { useGenerateStore } from '@/state/generateStore'
import type { TokenUsageCounts } from '@/state/generateStore'
import { useRun, useStartCampaign, useStopRun } from '@/hooks/useRuns'
import { useStreamCampaign } from '@/hooks/useGenerate'
import { useAddToQueue } from '@/hooks/useContent'
import ContentTypeGrid from './ContentTypeGrid'
import PipelinePanel from './PipelinePanel'
import ContentCard from './ContentCard'
import LiveStreamPanel from './LiveStreamPanel'
import styles from './NewCampaignTab.module.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface NewCampaignTabProps {
  clientId: string
}

// ─── Content type labels ──────────────────────────────────────────────────────

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  facebook_post: 'Facebook Post',
  twitter_post: 'Twitter / X',
  instagram_post: 'Instagram Post',
  linkedin_post: 'LinkedIn Post',
  blog_seo: 'Blog / SEO',
  email_outreach: 'Email Outreach',
  facebook_ad: 'Facebook Ad',
  instagram_ad: 'Instagram Ad',
  google_ads: 'Google Ads',
  gbp_post: 'Google Business',
}

// ─── Channel key mapping ──────────────────────────────────────────────────────

const CHANNEL_TO_CONTENT_TYPE: Record<string, ContentType> = {
  facebook: 'facebook_post',
  twitter: 'twitter_post',
  linkedin: 'linkedin_post',
  instagram: 'instagram_post',
  gbp: 'gbp_post',
  blog: 'blog_seo',
  email: 'email_outreach',
  google_ads: 'google_ads',
}

function streamItemToResult(item: StreamPublishQueueItem): AutonomousResult | null {
  const content_type = CHANNEL_TO_CONTENT_TYPE[item.channel]
  if (!content_type) return null
  return {
    content_type,
    content: item.content,
    compliance: {
      approved: item.status === 'approved',
      issues: item.status !== 'approved' ? ['Pending review'] : [],
      suggested_fixes: [],
    },
    revision_count: 0,
    seo_metadata: item.slug
      ? { meta_title: item.title ?? '', meta_description: item.meta_description ?? '', slug: item.slug }
      : undefined,
  }
}

// ─── Buyer journey stage ──────────────────────────────────────────────────────

const STAGE_OPTIONS: MultiselectProps.Option[] = [
  { value: 'awareness', label: 'Awareness — Top of funnel, brand discovery' },
  { value: 'consideration', label: 'Consideration — Mid-funnel, evaluating solutions' },
  { value: 'decision', label: 'Decision — Bottom of funnel, ready to buy' },
]

const STAGE_BADGE_COLOR: Record<BuyerJourneyStage, 'green' | 'blue' | 'red'> = {
  awareness: 'green',
  consideration: 'blue',
  decision: 'red',
}

// ─── Agent status labels ──────────────────────────────────────────────────────

const AGENT_STATUS_LABELS: Record<string, string> = {
  idle: 'Idle',
  running: 'Running',
  publishing: 'Publishing',
  complete: 'Complete',
  error: 'Error',
  stopped: 'Stopped',
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NewCampaignTab({ clientId }: NewCampaignTabProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedStages, setSelectedStages] = useState<BuyerJourneyStage[]>(['awareness'])
  const [queueStatuses, setQueueStatuses] = useState<Record<string, 'idle' | 'pushing' | 'queued' | 'error'>>({})
  const [queueErrors, setQueueErrors] = useState<Record<string, string>>({})
  const [stageOverrides, setStageOverrides] = useState<Record<string, BuyerJourneyStage>>({})

  const { agentStatus, setAgentStatus, runId, nodeStatuses, errorMessage, publishQueue, liveTokenUsage, activeModel, reset, setClientId, setBuyerJourneyStage, buyerJourneyStage, selectedTypes: persistedTypes, setSelectedTypes: persistTypes } = useGenerateStore()

  const urlRunId = searchParams.get('runId')

  // Local selection state for the idle config UI. On mount, restore from store when
  // returning mid-run so content type placeholders and pipeline panel stay populated.
  const [selectedTypes, setSelectedTypes] = useState<ContentType[]>(() =>
    persistedTypes.length > 0 ? persistedTypes : []
  )

  const { run, startPolling, stopPolling, refetch } = useRun(clientId, runId ?? '')
  const { startCampaign, loading } = useStartCampaign()
  const { stream, isStreaming } = useStreamCampaign()
  const { stopRun } = useStopRun()
  const { addToQueue } = useAddToQueue()

  // When navigating to a specific run via ?runId=... (History "View" or auto-run link),
  // open the SSE stream for that run. The backend replays node_runtime_data for completed
  // runs (Case A) or attaches to the live stream for in-progress runs (Case B), so the
  // campaign tab shows content regardless of trigger type (manual or auto).
  useEffect(() => {
    if (!urlRunId || urlRunId === runId) return
    void stream(clientId, urlRunId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlRunId])

  // ─── Polling / refetch effect ─────────────────────────────────────────────
  // While SSE is streaming, Zustand drives the live UI — no need to poll.
  // When the stream ends, do a single refetch to load final results from MongoDB.
  // If SSE never connects (isStreaming stays false while running), fall back to polling.

  const wasStreaming = useRef(false)

  useEffect(() => {
    if (isStreaming) {
      wasStreaming.current = true
      stopPolling()
      return
    }
    if (wasStreaming.current) {
      wasStreaming.current = false
      void refetch()
      return
    }
    if (agentStatus === 'running') {
      startPolling(3000)
    } else if (agentStatus === 'complete' || agentStatus === 'error' || agentStatus === 'stopped') {
      stopPolling()
    }
  }, [agentStatus, isStreaming, startPolling, stopPolling, refetch])

  // ─── Sync agentStatus from run.status ─────────────────────────────────────

  useEffect(() => {
    if (!run?.status) return
    // Never let stale GQL data downgrade a terminal SSE-sourced status.
    // SSE delivers 'complete'/'error'/'stopped' first; the DB write may lag,
    // causing the next refetch to return 'running' and create a second transition.
    if (agentStatus === 'error' || agentStatus === 'complete' || agentStatus === 'stopped') {
      stopPolling()
      return
    }
    if (run.status === 'complete' || run.status === 'stopped') {
      setAgentStatus(run.status)
      stopPolling()
    } else if (run.status === 'error') {
      setAgentStatus('error')
      stopPolling()
    } else if (run.status === 'running') {
      setAgentStatus('running')
    }
  }, [run?.status, agentStatus, setAgentStatus, stopPolling])

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleRun = async () => {
    if (!selectedTypes.length || !selectedStages.length) return
    try {
      setClientId(clientId)
      setBuyerJourneyStage(selectedStages[0])
      persistTypes([...selectedTypes])  // persist so it survives navigation
      const result = await startCampaign(clientId, [...selectedTypes], 'pipeline', selectedStages)
      const newRunId = result.data?.startCampaign.runId
      if (!newRunId) {
        setAgentStatus('error')
        return
      }
      void stream(clientId, newRunId)
    } catch {
      setAgentStatus('error')
    }
  }

  const handleStop = async () => {
    if (!runId) return
    await stopRun(clientId, runId)
    setAgentStatus('stopped')
    stopPolling()
  }

  const handleQueue = async (result: AutonomousResult, key?: string, stage?: BuyerJourneyStage) => {
    const k = key ?? result.content_type
    setQueueStatuses(prev => ({ ...prev, [k]: 'pushing' }))
    try {
      await addToQueue(
        clientId,
        result.content_type,
        result.content,
        runId,
        result.seo_metadata
          ? { metaTitle: result.seo_metadata.meta_title, metaDescription: result.seo_metadata.meta_description, slug: result.seo_metadata.slug }
          : undefined,
        stage,
      )
      setQueueStatuses(prev => ({ ...prev, [k]: 'queued' }))
    } catch (err) {
      setQueueStatuses(prev => ({ ...prev, [k]: 'error' }))
      setQueueErrors(prev => ({ ...prev, [k]: (err as Error).message }))
    }
  }

  const handleReset = () => {
    reset()  // clears store: selectedTypes, clientId, buyerJourneyStage, runId, etc.
    setSelectedTypes([])
    setSelectedStages([])
    setQueueStatuses({})
    setQueueErrors({})
    setStageOverrides({})
    setSearchParams({ tab: 'new-campaign' }, { replace: true })
  }

  // ─── Idle state ───────────────────────────────────────────────────────────

  const isIdle = agentStatus === 'idle'

  if (isIdle) {
    return (
      <Container
        header={
          <Header
            variant="h2"
            counter={`${selectedTypes.length} of 5 selected`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="primary"
                  disabled={selectedTypes.length === 0}
                  loading={loading || isStreaming}
                  onClick={() => { void handleRun() }}
                >
                  Run Campaign
                </Button>
              </SpaceBetween>
            }
          >
            New Campaign
          </Header>
        }
      >
        <SpaceBetween size="m">
          <SpaceBetween direction="horizontal" size="m">
            <FormField label="Buyer Journey Stages">
              <Multiselect
                selectedOptions={selectedStages.map(stage => ({ value: stage, label: STAGE_OPTIONS.find(s => s.value === stage)?.label || stage }))}
                onChange={({ detail }) => setSelectedStages(detail.selectedOptions.map(opt => opt.value as BuyerJourneyStage))}
                options={STAGE_OPTIONS}
                placeholder="Select one or more stages…"
              />
            </FormField>
            <FormField label="Channels">
              <ContentTypeGrid selected={selectedTypes} onChange={setSelectedTypes} />
            </FormField>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    )
  }

  // ─── Running / Complete state ─────────────────────────────────────────────

  const alertType = agentStatus === 'complete'
    ? 'success'
    : agentStatus === 'error'
      ? 'error'
      : 'info'

  const statusLabel = AGENT_STATUS_LABELS[agentStatus] ?? agentStatus

  // During streaming, derive the active node from SSE events (Zustand nodeStatuses) — GQL is stale.
  // Active = the first pipeline node that hasn't been marked done yet.
  const PIPELINE_ORDER = ['resolve_client', 'persona_builder', 'strategy_planner', 'campaign_writer', 'tone_checker', 'ethics_guardrail', 'output_formatter']
  const sseActiveNode = agentStatus === 'running'
    ? (PIPELINE_ORDER.find(n => nodeStatuses[n as NodeName] !== 'done') ?? null)
    : null
  const currentActiveNode = sseActiveNode ?? run?.active_node ?? null
  const activeNodeLabel = currentActiveNode
    ? currentActiveNode.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : null

  // During streaming, Apollo polling is stopped so run data is stale — use live Zustand counts.
  const isLiveStreaming = agentStatus === 'running'
  const displayTokenUsage: TokenUsageCounts = isLiveStreaming && liveTokenUsage.total_tokens > 0
    ? liveTokenUsage
    : (run?.token_usage ?? { input_tokens: 0, output_tokens: 0, total_tokens: 0 })
  const displayCostUsd = isLiveStreaming && liveTokenUsage.total_tokens > 0
    ? (liveTokenUsage.input_tokens * 3 + liveTokenUsage.output_tokens * 15) / 1_000_000
    : (run?.estimated_cost_usd ?? 0)
  const totalTokens = displayTokenUsage.total_tokens
  const estimatedCost = displayCostUsd

  const hasResults = (run?.results?.length ?? 0) > 0
  const isGenerating = Object.values(run?.content_type_statuses ?? {}).includes('generating')
  const showEmptyRight = !hasResults && !isGenerating && agentStatus !== 'running' && agentStatus !== 'error' && !(publishQueue?.length)

  const emptyRightMessage =
    agentStatus === 'stopped'
      ? 'Campaign was stopped before any content was generated.'
      : 'No content generated yet.'

  return (
    <SpaceBetween size="m">
      {/* Status bar */}
      <Alert
        type={alertType}
        header={agentStatus === 'error' ? 'Campaign failed' : undefined}
        action={
          agentStatus === 'running'
            ? (
                <Button variant="normal" onClick={() => { void handleStop() }}>
                  Stop
                </Button>
              )
            : (
                <Button variant="primary" onClick={handleReset}>
                  New Campaign
                </Button>
              )
        }
      >
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          {agentStatus === 'running' && <Box variant="span" className={styles.pulsingDot} aria-hidden="true" />}
          <Box fontWeight="bold">{statusLabel}</Box>
          {agentStatus === 'error' && (errorMessage || run?.errorMessage) && (
            <Box variant="span">
              {errorMessage || run?.errorMessage}{' '}
              {errorMessage && <Link onFollow={() => navigate('/settings?tab=model')}>Configure model</Link>}
            </Box>
          )}
          {activeNodeLabel && agentStatus === 'running' && (
            <Badge>{activeNodeLabel}</Badge>
          )}
          {buyerJourneyStage && (
            <Badge color={STAGE_BADGE_COLOR[buyerJourneyStage]}>
              {buyerJourneyStage.charAt(0).toUpperCase() + buyerJourneyStage.slice(1)}
            </Badge>
          )}
          {totalTokens > 0 && (
            <Box variant="span" color="text-body-secondary">
              {totalTokens.toLocaleString()} tokens · ${estimatedCost.toFixed(3)}
            </Box>
          )}
        </SpaceBetween>
      </Alert>

      {/* Two-column layout */}
      <div className={styles.runningLayout}>
        {/* Left: Pipeline panel */}
        <PipelinePanel
          nodeProgress={isStreaming ? [] : (run?.nodeProgress ?? [])}
          nodeStatuses={nodeStatuses as Record<string, NodeStatus>}
          activeNode={currentActiveNode}
          typeStatuses={run?.content_type_statuses ?? {}}
          selectedTypes={selectedTypes}
          estimatedCostUsd={displayCostUsd}
          tokenUsage={displayTokenUsage}
          progressPct={run?.progressPct}
          isFailed={agentStatus === 'error'}
          activeModel={activeModel}
        />

        {/* Right: Content cards */}
        <SpaceBetween size="m">
          {run?.results.map(result => (
            <ContentCard
              key={result.content_type}
              result={result}
              queueStatus={queueStatuses[result.content_type] ?? 'idle'}
              queueError={queueErrors[result.content_type]}
              onQueue={() => { void handleQueue(result, undefined, stageOverrides[result.content_type] ?? buyerJourneyStage ?? undefined) }}
              buyerJourneyStage={stageOverrides[result.content_type] ?? buyerJourneyStage ?? undefined}
              onStageChange={(stage) => setStageOverrides(prev => ({ ...prev, [result.content_type]: stage }))}
            />
          ))}
          {/* Live stream panel — shown while running instead of per-type spinner skeletons */}
          {agentStatus === 'running' && <LiveStreamPanel />}
          {/* Error-state placeholders — shown only on error for types without results */}
          {agentStatus === 'error' && selectedTypes
            .filter(type => !run?.results.some(r => r.content_type === type))
            .map(type => (
              <Container key={type} header={<Header variant="h3">{CONTENT_TYPE_LABELS[type]}</Header>}>
                <Box color="text-body-secondary">No content was generated for this channel.</Box>
              </Container>
            ))
          }
          {/* Stream-sourced content cards — shown when run.results empty but publishQueue captured from SSE */}
          {!hasResults && agentStatus === 'complete' && (publishQueue ?? []).map(item => {
            const result = streamItemToResult(item)
            if (!result) return null
            const itemStage = (item.buyer_journey_stage as BuyerJourneyStage | undefined)
            return (
              <ContentCard
                key={item.slot_id}
                result={result}
                queueStatus={queueStatuses[item.slot_id] ?? 'idle'}
                queueError={queueErrors[item.slot_id]}
                onQueue={() => { void handleQueue(result, item.slot_id, stageOverrides[item.slot_id] ?? itemStage ?? buyerJourneyStage ?? undefined) }}
                buyerJourneyStage={stageOverrides[item.slot_id] ?? itemStage ?? buyerJourneyStage ?? undefined}
                onStageChange={(stage) => setStageOverrides(prev => ({ ...prev, [item.slot_id]: stage }))}
              />
            )
          })}
          {/* Empty state when nothing was generated and no placeholders */}
          {showEmptyRight && (
            <Container>
              <Box textAlign="center" padding="xl" color="text-body-secondary">
                {emptyRightMessage}
              </Box>
            </Container>
          )}
        </SpaceBetween>
      </div>
    </SpaceBetween>
  )
}
